import React, { useState, useEffect } from 'react';
import {
  DollarSign,
  Percent,
  AlertCircle,
  Save,
  RefreshCw,
  TrendingUp,
  Award,
  Calendar
} from 'lucide-react';
import { getPriceTiers } from '../../../api/wholesale/pricing';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const PricingSettingsPage = () => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Global pricing settings
  const [settings, setSettings] = useState({
    default_markup_percentage: 15,
    minimum_profit_margin: 5,
    discount_approval_threshold: 20,
    bulk_order_threshold: 100,
    bulk_order_discount: 5,
    seasonal_discount_active: false,
    seasonal_discount_percentage: 10,
    seasonal_start_date: '',
    seasonal_end_date: '',
    auto_price_adjustment: false,
    price_adjustment_frequency: 'monthly'
  });

  // Campaign settings
  const [campaigns, setCampaigns] = useState([
    {
      id: 1,
      name: 'New Year Promo',
      discount_percentage: 15,
      start_date: '2025-01-01',
      end_date: '2025-01-31',
      active: false,
      min_order_value: 500000
    }
  ]);

  const fetchTiers = async () => {
    try {
      const response = await getPriceTiers();
      const data = response?.data || response || [];
      setTiers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching tiers:', err);
    }
  };

  useEffect(() => {
    fetchTiers();
    setLoading(false);
  }, []);

  const handleSettingChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage('');

    try {
      // TODO: Implement API call to save settings
      // await savePricingSettings(settings);

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setSuccessMessage('Pricing settings saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save pricing settings');
    } finally {
      setSaving(false);
    }
  };

  const handleResetSettings = () => {
    setSettings({
      default_markup_percentage: 15,
      minimum_profit_margin: 5,
      discount_approval_threshold: 20,
      bulk_order_threshold: 100,
      bulk_order_discount: 5,
      seasonal_discount_active: false,
      seasonal_discount_percentage: 10,
      seasonal_start_date: '',
      seasonal_end_date: '',
      auto_price_adjustment: false,
      price_adjustment_frequency: 'monthly'
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Pricing Rules & Settings</h1>
        <p className="text-gray-500 mt-1">
          Configure pricing rules, markups, and approval thresholds
        </p>
      </div>

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {successMessage}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {error}
        </div>
      )}

      {/* Default Pricing Rules */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Percent className="h-5 w-5 text-emerald-600" />
          Default Pricing Rules
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Markup Percentage
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={settings.default_markup_percentage}
                onChange={(e) =>
                  handleSettingChange('default_markup_percentage', parseFloat(e.target.value))
                }
                min="0"
                max="100"
                step="0.1"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
              <span className="text-gray-600">%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Applied to base cost when calculating selling price
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum Profit Margin
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={settings.minimum_profit_margin}
                onChange={(e) =>
                  handleSettingChange('minimum_profit_margin', parseFloat(e.target.value))
                }
                min="0"
                max="100"
                step="0.1"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
              <span className="text-gray-600">%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Minimum acceptable profit margin on orders</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Discount Approval Threshold
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={settings.discount_approval_threshold}
                onChange={(e) =>
                  handleSettingChange('discount_approval_threshold', parseFloat(e.target.value))
                }
                min="0"
                max="100"
                step="1"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
              <span className="text-gray-600">%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Discounts above this require manager approval
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Price Adjustment Frequency
            </label>
            <select
              value={settings.price_adjustment_frequency}
              onChange={(e) => handleSettingChange('price_adjustment_frequency', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            >
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="manual">Manual Only</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">How often to review and adjust prices</p>
          </div>
        </div>

        <div className="mt-6">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={settings.auto_price_adjustment}
              onChange={(e) => handleSettingChange('auto_price_adjustment', e.target.checked)}
              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-sm font-medium text-gray-700">
              Enable automatic price adjustments based on cost changes
            </span>
          </label>
          <p className="text-xs text-gray-500 ml-6 mt-1">
            Automatically adjust selling prices when supplier costs change
          </p>
        </div>
      </div>

      {/* Bulk Order Discounts */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-blue-600" />
          Bulk Order Discounts
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bulk Order Threshold (Tonnage)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={settings.bulk_order_threshold}
                onChange={(e) =>
                  handleSettingChange('bulk_order_threshold', parseFloat(e.target.value))
                }
                min="0"
                step="10"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
              <span className="text-gray-600">tons</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Orders above this tonnage qualify for bulk discount
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bulk Order Discount
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={settings.bulk_order_discount}
                onChange={(e) =>
                  handleSettingChange('bulk_order_discount', parseFloat(e.target.value))
                }
                min="0"
                max="50"
                step="0.5"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
              <span className="text-gray-600">%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Additional discount for bulk orders</p>
          </div>
        </div>
      </div>

      {/* Seasonal Promotions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-purple-600" />
          Seasonal Promotions
        </h2>

        <div className="mb-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={settings.seasonal_discount_active}
              onChange={(e) => handleSettingChange('seasonal_discount_active', e.target.checked)}
              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-sm font-medium text-gray-700">Enable seasonal promotions</span>
          </label>
        </div>

        {settings.seasonal_discount_active && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Seasonal Discount
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={settings.seasonal_discount_percentage}
                  onChange={(e) =>
                    handleSettingChange('seasonal_discount_percentage', parseFloat(e.target.value))
                  }
                  min="0"
                  max="50"
                  step="0.5"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
                <span className="text-gray-600">%</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
              <input
                type="date"
                value={settings.seasonal_start_date}
                onChange={(e) => handleSettingChange('seasonal_start_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <input
                type="date"
                value={settings.seasonal_end_date}
                onChange={(e) => handleSettingChange('seasonal_end_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* Tier-Based Pricing Overview */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Award className="h-5 w-5 text-yellow-600" />
          Current Tier Pricing Configuration
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Tier-specific pricing can be managed in the Pricing Tiers page.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {tiers.map((tier) => (
            <div key={tier.id} className="border border-gray-200 rounded-lg p-4">
              <p className="font-bold text-gray-900 mb-2">{tier.tier_name}</p>
              <div className="space-y-1 text-sm text-gray-600">
                <p>
                  Discount: {tier.discount_percentage_min}% - {tier.discount_percentage_max}%
                </p>
                <p>Default: {tier.default_discount_percentage}%</p>
                <p>
                  Payment Terms: {tier.payment_terms_min}-{tier.payment_terms_max} days
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Active Campaigns */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-green-600" />
          Active Pricing Campaigns
        </h2>

        <div className="space-y-4">
          {campaigns.map((campaign) => (
            <div key={campaign.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-medium text-gray-900">{campaign.name}</h3>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${campaign.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
                    >
                      {campaign.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                    <div>
                      <p className="text-gray-500">Discount</p>
                      <p className="font-medium text-gray-900">{campaign.discount_percentage}%</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Min Order</p>
                      <p className="font-medium text-gray-900">
                        ₦{campaign.min_order_value.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Start Date</p>
                      <p className="font-medium text-gray-900">
                        {new Date(campaign.start_date).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">End Date</p>
                      <p className="font-medium text-gray-900">
                        {new Date(campaign.end_date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <p className="text-gray-500">Campaign management coming soon</p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <button
          onClick={handleResetSettings}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Reset to Defaults
        </button>

        <button
          onClick={handleSaveSettings}
          disabled={saving}
          className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
        >
          <Save className="h-4 w-4" />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Info Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium mb-1">Note:</p>
            <p>
              Changes to pricing settings will affect new orders only. Existing orders and quotes
              will maintain their original pricing. For tier-specific settings, use the Pricing
              Tiers configuration page.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingSettingsPage;
